import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getBankingSummary, getBankTransactions, createBankTransaction, deleteBankTransaction, importBankCsv, autoReconcile, getBankCashflow, classifyTransactions } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { PageTransition, StaggerContainer, StaggerItem } from '../components/motion/Animations';
import { toast } from 'sonner';
import PageHelpButton from '../components/help/PageHelpButton';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, ComposedChart, Legend
} from 'recharts';
import {
  Landmark, Plus, Upload, Search, Trash2, ArrowUpRight, ArrowDownRight,
  CheckCircle2, Circle, Loader2, Zap, FileSpreadsheet, TrendingUp, TrendingDown, Scale, Brain, Tag
} from 'lucide-react';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(amount || 0);

const BankingPage = () => {
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [cashflow, setCashflow] = useState([]);
  const fileRef = useRef(null);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], description: '', amount: '', type: 'income', reference: '', category: '', notes: '' });

  const fetchData = useCallback(async () => {
    try {
      const [sumRes, txnRes, cfRes] = await Promise.all([
        getBankingSummary(),
        getBankTransactions(filter !== 'all' ? { reconciled: filter === 'reconciled' } : {}),
        getBankCashflow()
      ]);
      setSummary(sumRes.data);
      setTransactions(txnRes.data);
      setCashflow(cfRes.data);
    } catch { toast.error('Error cargando datos bancarios'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount) { toast.error('Descripción y monto son obligatorios'); return; }
    try {
      await createBankTransaction({ ...form, amount: parseFloat(form.amount) * (form.type === 'expense' ? -1 : 1) });
      toast.success('Movimiento registrado');
      setShowModal(false);
      setForm({ date: new Date().toISOString().split('T')[0], description: '', amount: '', type: 'income', reference: '', category: '', notes: '' });
      fetchData();
    } catch { toast.error('Error al registrar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este movimiento?')) return;
    try { await deleteBankTransaction(id); toast.success('Eliminado'); fetchData(); }
    catch { toast.error('Error al eliminar'); }
  };

  const handleImportCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const { data } = await importBankCsv(file);
      toast.success(`${data.imported} movimientos importados`);
      if (data.errors > 0) toast.warning(`${data.errors} errores en la importación`);
      fetchData();
    } catch { toast.error('Error importando CSV'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleAutoReconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await autoReconcile();
      if (data.matched > 0) toast.success(`${data.matched} movimientos conciliados automáticamente`);
      else toast.info('No se encontraron coincidencias');
      fetchData();
    } catch { toast.error('Error en conciliación'); }
    finally { setReconciling(false); }
  };

  const handleClassify = async () => {
    setClassifying(true);
    try {
      const { data } = await classifyTransactions();
      if (data.classified > 0) toast.success(`${data.classified} movimientos clasificados con IA`);
      else toast.info(data.message || 'No hay movimientos sin clasificar');
      fetchData();
    } catch { toast.error('Error en clasificación IA'); }
    finally { setClassifying(false); }
  };

  const filtered = transactions.filter(t =>
    t.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.reference?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div></div>;
  }

  return (
    <PageTransition>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight text-stone-950" data-testid="banking-title">Banca</h1>
              <p className="text-stone-400 text-sm mt-1">Gestiona movimientos bancarios y conciliación</p>
            </div>
            <PageHelpButton moduleKey="banking" variant="icon" />
          </div>
          <div className="flex gap-2 w-full sm:w-auto flex-wrap">
            <input type="file" ref={fileRef} className="hidden" accept=".csv" onChange={handleImportCsv} />
            <Button variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="import-csv-button">
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Importar CSV
            </Button>
            <Button variant="outline" className="rounded-full" onClick={handleAutoReconcile} disabled={reconciling} data-testid="auto-reconcile-button">
              {reconciling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Conciliar
            </Button>
            <Button variant="outline" className="rounded-full" onClick={handleClassify} disabled={classifying} data-testid="classify-button">
              {classifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
              Clasificar IA
            </Button>
            <Button className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50" onClick={() => setShowModal(true)} data-testid="add-transaction-button">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Movimiento
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <StaggerContainer className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StaggerItem>
            <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5" data-testid="bank-income-card">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                <span className="text-[10px] sm:text-xs uppercase tracking-widest font-semibold text-stone-400">Ingresos</span>
              </div>
              <p className="text-xl font-bold text-emerald-600">{formatCurrency(summary?.total_income)}</p>
            </div>
          </StaggerItem>
          <StaggerItem>
            <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5" data-testid="bank-expenses-card">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="w-4 h-4 text-red-500" />
                <span className="text-[10px] sm:text-xs uppercase tracking-widest font-semibold text-stone-400">Gastos</span>
              </div>
              <p className="text-xl font-bold text-red-500">{formatCurrency(summary?.total_expenses)}</p>
            </div>
          </StaggerItem>
          <StaggerItem>
            <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5" data-testid="bank-balance-card">
              <div className="flex items-center gap-2 mb-2">
                <Scale className="w-4 h-4 text-[#0EA5E9]" />
                <span className="text-[10px] sm:text-xs uppercase tracking-widest font-semibold text-stone-400">Balance</span>
              </div>
              <p className={`text-xl font-bold ${(summary?.balance || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{formatCurrency(summary?.balance)}</p>
            </div>
          </StaggerItem>
          <StaggerItem>
            <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5" data-testid="bank-reconciled-card">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4 text-[#FBBF24]" />
                <span className="text-[10px] sm:text-xs uppercase tracking-widest font-semibold text-stone-400">Conciliados</span>
              </div>
              <p className="text-xl font-bold text-stone-800">{summary?.reconciled || 0}<span className="text-sm text-stone-400 font-normal">/{summary?.total_transactions || 0}</span></p>
            </div>
          </StaggerItem>
        </StaggerContainer>

        {/* Cash Flow Chart */}
        {cashflow.some(m => m.income > 0 || m.expense > 0) && (
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-6" data-testid="cashflow-chart">
            <h3 className="text-lg font-heading font-bold text-stone-900 mb-4">Flujo de Caja Mensual</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={cashflow}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#78716C' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#78716C' }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }} />
                  <Legend />
                  <Bar dataKey="income" fill="#10B981" name="Ingresos" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="expense" fill="#F43F5E" name="Gastos" radius={[6, 6, 0, 0]} />
                  <Line type="monotone" dataKey="balance" stroke="#0EA5E9" strokeWidth={2.5} name="Balance" dot={{ fill: '#0EA5E9', r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input placeholder="Buscar movimientos..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11 rounded-xl bg-stone-50 border-transparent" data-testid="search-transactions-input" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-full sm:w-48 rounded-xl bg-stone-50 border-transparent" data-testid="filter-reconciled-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="reconciled">Conciliados</SelectItem>
              <SelectItem value="unreconciled">Sin conciliar</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Transactions List */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100" data-testid="no-transactions">
            <Landmark className="w-14 h-14 text-stone-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-stone-800 mb-1">Sin movimientos bancarios</h3>
            <p className="text-stone-400 text-sm mb-6">Importa un extracto CSV o añade movimientos manualmente</p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()}>
                <Upload className="w-4 h-4 mr-2" /> Importar CSV
              </Button>
              <Button className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full" onClick={() => setShowModal(true)}>
                <Plus className="w-4 h-4 mr-2" /> Añadir
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 overflow-hidden">
            <div className="divide-y divide-stone-50">
              {filtered.map((txn) => (
                <div key={txn.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-orange-50/50 transition-colors" data-testid={`transaction-${txn.id}`}>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${txn.amount >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                    {txn.amount >= 0 ? <ArrowDownRight className="w-4 h-4 text-emerald-500" /> : <ArrowUpRight className="w-4 h-4 text-red-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800 truncate">{txn.description}</p>
                    <div className="flex items-center gap-2 text-xs text-stone-400">
                      <span>{txn.date}</span>
                      {txn.reference && <span>Ref: {txn.reference}</span>}
                      {txn.category && txn.category !== '' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-sky-50 text-sky-600 rounded-full text-[10px] font-medium">
                          <Tag className="w-2.5 h-2.5" />{txn.category}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {txn.reconciled ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" title="Conciliado" />
                    ) : (
                      <Circle className="w-4 h-4 text-stone-300" title="Sin conciliar" />
                    )}
                    <span className={`font-bold text-sm tabular-nums ${txn.amount >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {txn.amount >= 0 ? '+' : ''}{formatCurrency(txn.amount)}
                    </span>
                    <button onClick={() => handleDelete(txn.id)} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-txn-${txn.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-stone-300 hover:text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Create Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading font-extrabold">Nuevo Movimiento</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Fecha</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})}
                    className="rounded-xl bg-stone-50 border-transparent" data-testid="txn-date-input" />
                </div>
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                    <SelectTrigger className="rounded-xl bg-stone-50 border-transparent" data-testid="txn-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="income">Ingreso</SelectItem>
                      <SelectItem value="expense">Gasto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Descripción *</Label>
                <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                  placeholder="Concepto del movimiento" className="rounded-xl bg-stone-50 border-transparent" required data-testid="txn-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Importe *</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})}
                    placeholder="0.00" className="rounded-xl bg-stone-50 border-transparent font-bold" required data-testid="txn-amount-input" />
                </div>
                <div className="space-y-2">
                  <Label>Referencia</Label>
                  <Input value={form.reference} onChange={(e) => setForm({...form, reference: e.target.value})}
                    className="rounded-xl bg-stone-50 border-transparent" data-testid="txn-reference-input" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1 rounded-full">Cancelar</Button>
                <Button type="submit" className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full" data-testid="save-transaction-button">Registrar</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </PageTransition>
  );
};

export default BankingPage;
